import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPut, apiPost } from '../utils/api';

type SettingRow = {
  id: string;
  key: string;
  value: string;
  valueType: 'NUMBER' | 'BOOLEAN' | 'STRING' | 'JSON';
  unit?: string | null;
  minValue?: number | null;
  maxValue?: number | null;
  description?: string | null;
  updatedAt: string;
  updatedByUser?: {
    id: string;
    name: string;
    email: string;
  } | null;
};

type CohortRow = {
  id: string;
  playerId: string;
  playerName: string | null;
  singlesLevel: string | null;
  pwr: number | null;
  telegramLinked: boolean;
  enabled: boolean;
  isActiveNow: boolean;
  note: string | null;
  grantedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  grantedByUser?: { id: string; name: string; email: string } | null;
};

type CohortResponse = {
  success: boolean;
  data: {
    activeCount: number;
    items: CohortRow[];
  };
};

type SearchPlayer = {
  id: string;
  name: string;
  singlesLevel?: string | null;
  eloRatings?: Array<{ discipline: string; currentRating: number }>;
  playerTelegramAccounts?: Array<{ id: string }>;
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU');
};

const PlaySettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [rows, setRows] = useState<SettingRow[]>([]);
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cohortRows, setCohortRows] = useState<CohortRow[]>([]);
  const [cohortLoading, setCohortLoading] = useState(false);
  const [cohortSearch, setCohortSearch] = useState('');
  const [cohortLevel, setCohortLevel] = useState('');
  const [cohortStatus, setCohortStatus] = useState<'all' | 'active' | 'inactive' | 'expired'>('active');
  const [cohortActiveCount, setCohortActiveCount] = useState(0);
  const [cohortSelectedIds, setCohortSelectedIds] = useState<string[]>([]);
  const [cohortNote, setCohortNote] = useState('');
  const [cohortExpiresAt, setCohortExpiresAt] = useState('');
  const [playerSearchText, setPlayerSearchText] = useState('');
  const [playerSearchRows, setPlayerSearchRows] = useState<SearchPlayer[]>([]);
  const [playerSearchLoading, setPlayerSearchLoading] = useState(false);
  const [playerSelectedIds, setPlayerSelectedIds] = useState<string[]>([]);

  const hasChanges = useMemo(() => {
    return rows.some((row) => String(draftValues[row.key] ?? row.value) !== String(row.value));
  }, [rows, draftValues]);

  const accessModeValue = String(draftValues.playAccessMode ?? rows.find((r) => r.key === 'playAccessMode')?.value ?? 'DISABLED').toUpperCase();
  const accessModeLabel = accessModeValue === 'DISABLED'
    ? 'Выключено'
    : accessModeValue === 'TEST_COHORT'
      ? 'Только тестовая группа'
      : 'Доступно всем';

  const getInitialMode = (): string => {
    const row = rows.find((r) => r.key === 'playAccessMode');
    return String(row?.value || '').toUpperCase();
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiGet<{ success: boolean; data: SettingRow[] }>('/play/admin/settings');
      const list = Array.isArray(data?.data) ? data.data : [];
      setRows(list);
      setDraftValues(
        list.reduce<Record<string, string>>((acc, row) => {
          acc[row.key] = String(row.value);
          return acc;
        }, {})
      );
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить настройки TTM PLAY');
    } finally {
      setLoading(false);
    }
  };

  const loadCohort = async () => {
    setCohortLoading(true);
    try {
      const q = new URLSearchParams();
      if (cohortSearch.trim()) q.set('search', cohortSearch.trim());
      if (cohortLevel.trim()) q.set('level', cohortLevel.trim());
      if (cohortStatus) q.set('status', cohortStatus);
      q.set('limit', '300');
      const data = await apiGet<CohortResponse>(`/play/admin/cohort?${q.toString()}`);
      setCohortRows(data?.data?.items || []);
      setCohortActiveCount(Number(data?.data?.activeCount || 0));
      setCohortSelectedIds([]);
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить тестовую группу');
    } finally {
      setCohortLoading(false);
    }
  };

  const searchPlayers = async () => {
    if (!playerSearchText.trim()) {
      setPlayerSearchRows([]);
      return;
    }
    setPlayerSearchLoading(true);
    try {
      const q = new URLSearchParams();
      q.set('search', playerSearchText.trim());
      q.set('limit', '20');
      q.set('page', '1');
      q.set('isActive', 'true');
      const data = await apiGet<{ players: SearchPlayer[] }>(`/players?${q.toString()}`);
      setPlayerSearchRows(Array.isArray(data?.players) ? data.players : []);
    } catch (e: any) {
      setError(e?.message || 'Не удалось выполнить поиск игроков');
    } finally {
      setPlayerSearchLoading(false);
    }
  };

  const ensureReason = (): string | null => {
    const value = reason.trim();
    if (!value) {
      setError('Для изменения rollout/cohort требуется причина (reason)');
      return null;
    }
    return value;
  };

  const setCohortMember = async (playerId: string, enabled: boolean) => {
    const reasonValue = ensureReason();
    if (!reasonValue) return;
    setError('');
    try {
      await apiPost('/play/admin/cohort/member', {
        playerId,
        enabled,
        note: cohortNote.trim() || undefined,
        expiresAt: cohortExpiresAt || undefined,
        reason: reasonValue
      });
      await loadCohort();
      setSuccess(enabled ? 'Игрок добавлен в тестовую группу' : 'Игрок исключён из тестовой группы');
    } catch (e: any) {
      setError(e?.message || 'Ошибка обновления тестовой группы');
    }
  };

  const bulkSetCohort = async (enabled: boolean, ids: string[]) => {
    const reasonValue = ensureReason();
    if (!reasonValue) return;
    if (!ids.length) {
      setError('Нужно выбрать хотя бы одного игрока');
      return;
    }
    setError('');
    try {
      await apiPost('/play/admin/cohort/bulk', {
        playerIds: ids,
        enabled,
        note: cohortNote.trim() || undefined,
        expiresAt: cohortExpiresAt || undefined,
        reason: reasonValue
      });
      await loadCohort();
      setSuccess(enabled ? 'Массовое добавление выполнено' : 'Массовое исключение выполнено');
      setPlayerSelectedIds([]);
      setCohortSelectedIds([]);
    } catch (e: any) {
      setError(e?.message || 'Ошибка массового изменения тестовой группы');
    }
  };

  useEffect(() => {
    load();
    loadCohort();
  }, []);

  const validateDrafts = (): string | null => {
    for (const row of rows) {
      const value = String(draftValues[row.key] ?? row.value).trim();
      if (row.valueType === 'BOOLEAN' && !['true', 'false'].includes(value.toLowerCase())) {
        return `Настройка ${row.key}: допустимы только true/false`;
      }
      if (row.valueType === 'NUMBER') {
        const num = Number(value);
        if (!Number.isFinite(num)) return `Настройка ${row.key}: должно быть числом`;
        if (row.minValue !== null && row.minValue !== undefined && num < row.minValue) {
          return `Настройка ${row.key}: значение должно быть >= ${row.minValue}`;
        }
        if (row.maxValue !== null && row.maxValue !== undefined && num > row.maxValue) {
          return `Настройка ${row.key}: значение должно быть <= ${row.maxValue}`;
        }
      }
    }
    return null;
  };

  const save = async () => {
    setError('');
    setSuccess('');
    const validationError = validateDrafts();
    if (validationError) {
      setError(validationError);
      return;
    }

    const values = rows.reduce<Record<string, string>>((acc, row) => {
      const nextValue = String(draftValues[row.key] ?? row.value);
      if (nextValue !== String(row.value)) {
        acc[row.key] = nextValue;
      }
      return acc;
    }, {});

    if (Object.keys(values).length === 0) {
      setSuccess('Изменений нет');
      return;
    }

    const initialMode = getInitialMode();
    const nextMode = String(values.playAccessMode ?? draftValues.playAccessMode ?? initialMode).toUpperCase();
    if (initialMode && nextMode && initialMode !== nextMode) {
      if (initialMode === 'PUBLIC' && nextMode === 'TEST_COHORT') {
        const ok = window.confirm('Игроки вне тестовой группы больше не смогут создавать новые предложения или откликаться. Существующие активные сценарии не будут автоматически удалены.\n\nПродолжить?');
        if (!ok) return;
      }
      if ((initialMode === 'TEST_COHORT' || initialMode === 'PUBLIC') && nextMode === 'DISABLED') {
        const ok = window.confirm('Новые действия и рассылки TTM PLAY будут остановлены. Существующие данные сохранятся.\n\nПродолжить?');
        if (!ok) return;
      }
    }
    if (nextMode === 'PUBLIC') {
      const ok = window.confirm('Режим откроет TTM PLAY всем активным игрокам, соответствующим общим правилам.\n\nПродолжить?');
      if (!ok) return;
    }

    setSaving(true);
    try {
      await apiPut('/play/admin/settings', {
        values,
        reason: reason.trim() || undefined
      });
      setSuccess('Настройки TTM PLAY сохранены');
      setReason('');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Сбросить все настройки TTM PLAY к seed/default значениям?')) return;
    setResetting(true);
    setError('');
    setSuccess('');
    try {
      await apiPost('/play/admin/settings/reset-defaults', {
        reason: reason.trim() || 'RESET_TO_DEFAULTS'
      });
      setSuccess('Настройки сброшены к значениям по умолчанию');
      setReason('');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Ошибка сброса настроек');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Настройки TTM PLAY</h2>
        <p className="text-sm text-gray-600 mt-1">
          Изменения применяются без перезапуска и влияют на новые предложения.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        <div>
          <label className="block text-sm text-gray-700 mb-1">Причина изменения (для аудита)</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full border rounded px-3 py-2"
            placeholder="Например: корректировка волны рассылки"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={save}
            disabled={saving || loading || !hasChanges}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button
            onClick={resetDefaults}
            disabled={resetting || loading}
            className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
          >
            {resetting ? 'Сброс...' : 'Сбросить defaults'}
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="px-4 py-2 border rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Обновить
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">Доступ и тестирование</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Режим доступа</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={String(draftValues.playAccessMode ?? 'DISABLED').toUpperCase()}
              onChange={(e) => setDraftValues((prev) => ({ ...prev, playAccessMode: e.target.value }))}
            >
              <option value="DISABLED">Выключено</option>
              <option value="TEST_COHORT">Только тестовая группа</option>
              <option value="PUBLIC">Доступно всем</option>
            </select>
            <div className="text-xs text-gray-500 mt-1">Текущий режим: {accessModeLabel}</div>
            <div className="text-xs text-gray-600 mt-2">
              <div><b>DISABLED</b>: Функция недоступна игрокам. Меню, новые действия и рассылки отключены.</div>
              <div><b>TEST_COHORT</b>: Функция доступна только активным участникам тестовой группы.</div>
              <div><b>PUBLIC</b>: Функция доступна всем подходящим игрокам.</div>
              <div className="text-amber-700 mt-1">Внимание: режим PUBLIC откроет TTM PLAY всем активным игрокам по общим правилам.</div>
            </div>
          </div>
          <div className="text-sm text-gray-700">
            <div className="font-medium mb-1">Тестовая группа</div>
            <div>Активных тестеров: <span className="font-semibold">{cohortActiveCount}</span></div>
            <div className="text-xs text-gray-500 mt-1">Используется только в режиме TEST_COHORT.</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={String(draftValues.playWebsiteMenuVisible ?? 'true').toLowerCase() === 'true'}
              onChange={(e) => setDraftValues((prev) => ({ ...prev, playWebsiteMenuVisible: String(e.target.checked) }))}
            />
            Показывать TTM PLAY в главном меню
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={String(draftValues.playWebsiteHomepageVisible ?? 'true').toLowerCase() === 'true'}
              onChange={(e) => setDraftValues((prev) => ({ ...prev, playWebsiteHomepageVisible: String(e.target.checked) }))}
            />
            Показывать блок TTM PLAY на главной странице
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={String(draftValues.playBotMenuVisible ?? 'true').toLowerCase() === 'true'}
              onChange={(e) => setDraftValues((prev) => ({ ...prev, playBotMenuVisible: String(e.target.checked) }))}
            />
            Показывать TTM PLAY в меню Telegram-бота
          </label>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">Управление тестовой группой</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input
            type="text"
            value={cohortSearch}
            onChange={(e) => setCohortSearch(e.target.value)}
            className="border rounded px-2 py-1"
            placeholder="Фильтр по имени"
          />
          <input
            type="text"
            value={cohortLevel}
            onChange={(e) => setCohortLevel(e.target.value)}
            className="border rounded px-2 py-1"
            placeholder="Фильтр по уровню (S-6)"
          />
          <select
            value={cohortStatus}
            onChange={(e) => setCohortStatus(e.target.value as any)}
            className="border rounded px-2 py-1"
          >
            <option value="all">Все</option>
            <option value="active">Активные</option>
            <option value="inactive">Отключённые</option>
            <option value="expired">Истекшие</option>
          </select>
          <button
            onClick={loadCohort}
            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            disabled={cohortLoading}
          >
            Обновить группу
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <input
            type="text"
            value={cohortNote}
            onChange={(e) => setCohortNote(e.target.value)}
            className="border rounded px-2 py-1"
            placeholder="Комментарий (опц.)"
          />
          <input
            type="datetime-local"
            value={cohortExpiresAt}
            onChange={(e) => setCohortExpiresAt(e.target.value)}
            className="border rounded px-2 py-1"
          />
          <div className="text-xs text-gray-500 flex items-center">Срок доступа используется для временного включения.</div>
        </div>

        <div className="border rounded p-2 space-y-2">
          <div className="flex gap-2">
            <input
              type="text"
              value={playerSearchText}
              onChange={(e) => setPlayerSearchText(e.target.value)}
              className="flex-1 border rounded px-2 py-1"
              placeholder="Поиск игроков для добавления"
            />
            <button
              onClick={searchPlayers}
              className="px-3 py-1 border rounded hover:bg-gray-50"
              disabled={playerSearchLoading}
            >
              Найти
            </button>
            <button
              onClick={() => bulkSetCohort(true, playerSelectedIds)}
              className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Массово добавить
            </button>
          </div>
          <div className="max-h-40 overflow-auto text-sm">
            {playerSearchRows.map((row) => {
              const singlesPwr = row.eloRatings?.find((r) => String(r.discipline).toUpperCase() === 'SINGLES')?.currentRating ?? null;
              const selected = playerSelectedIds.includes(row.id);
              return (
                <div key={row.id} className="flex items-center justify-between border-b py-1">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={(e) => {
                        if (e.target.checked) setPlayerSelectedIds((prev) => Array.from(new Set([...prev, row.id])));
                        else setPlayerSelectedIds((prev) => prev.filter((id) => id !== row.id));
                      }}
                    />
                    <span>{row.name}</span>
                    <span className="text-gray-500">{row.singlesLevel || '—'} • PWR {singlesPwr ?? '—'}</span>
                  </label>
                  <button
                    onClick={() => setCohortMember(row.id, true)}
                    className="px-2 py-0.5 text-xs rounded bg-green-600 text-white"
                  >
                    Добавить
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => bulkSetCohort(false, cohortSelectedIds)}
            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Массово удалить/отключить
          </button>
        </div>

        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-2 py-1 text-left"></th>
                <th className="px-2 py-1 text-left">Игрок</th>
                <th className="px-2 py-1 text-left">Уровень</th>
                <th className="px-2 py-1 text-left">PWR</th>
                <th className="px-2 py-1 text-left">Telegram</th>
                <th className="px-2 py-1 text-left">Статус</th>
                <th className="px-2 py-1 text-left">Срок</th>
                <th className="px-2 py-1 text-left">Комментарий</th>
                <th className="px-2 py-1 text-left">Кто добавил</th>
                <th className="px-2 py-1 text-left">Дата добавления</th>
                <th className="px-2 py-1 text-left">Действия</th>
              </tr>
            </thead>
            <tbody>
              {cohortLoading && (
                <tr>
                  <td className="px-2 py-2 text-gray-500" colSpan={11}>Загрузка...</td>
                </tr>
              )}
              {!cohortLoading && cohortRows.map((row) => {
                const selected = cohortSelectedIds.includes(row.playerId);
                return (
                  <tr key={row.id} className="border-t">
                    <td className="px-2 py-1">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(e) => {
                          if (e.target.checked) setCohortSelectedIds((prev) => Array.from(new Set([...prev, row.playerId])));
                          else setCohortSelectedIds((prev) => prev.filter((id) => id !== row.playerId));
                        }}
                      />
                    </td>
                    <td className="px-2 py-1">{row.playerName || row.playerId}</td>
                    <td className="px-2 py-1">{row.singlesLevel || '—'}</td>
                    <td className="px-2 py-1">{row.pwr ?? '—'}</td>
                    <td className="px-2 py-1">{row.telegramLinked ? 'да' : 'нет'}</td>
                    <td className="px-2 py-1">{row.isActiveNow ? 'доступен' : (row.enabled ? 'истёк' : 'отключён')}</td>
                    <td className="px-2 py-1">{formatDateTime(row.expiresAt)}</td>
                    <td className="px-2 py-1">{row.note || '—'}</td>
                    <td className="px-2 py-1">{row.grantedByUser?.name || row.grantedByUser?.email || '—'}</td>
                    <td className="px-2 py-1">{formatDateTime(row.grantedAt)}</td>
                    <td className="px-2 py-1 space-x-1">
                      <button onClick={() => setCohortMember(row.playerId, true)} className="px-2 py-0.5 text-xs rounded bg-green-600 text-white">Включить</button>
                      <button onClick={() => setCohortMember(row.playerId, false)} className="px-2 py-0.5 text-xs rounded bg-red-600 text-white">Отключить</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {success && <div className="text-sm text-green-700">{success}</div>}

      <div className="bg-white rounded-lg shadow overflow-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-600">
              <th className="px-3 py-2 text-left">Параметр</th>
              <th className="px-3 py-2 text-left">Описание</th>
              <th className="px-3 py-2 text-left">Значение</th>
              <th className="px-3 py-2 text-left">Ед.</th>
              <th className="px-3 py-2 text-left">Диапазон</th>
              <th className="px-3 py-2 text-left">Изменено</th>
              <th className="px-3 py-2 text-left">Кем</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className="px-3 py-3 text-gray-500" colSpan={7}>Загрузка...</td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td className="px-3 py-3 text-gray-500" colSpan={7}>Нет данных</td>
              </tr>
            )}
            {!loading && rows.map((row) => (
              <tr key={row.key} className="border-t">
                <td className="px-3 py-2 font-medium whitespace-nowrap">{row.key}</td>
                <td className="px-3 py-2 text-gray-700">{row.description || '—'}</td>
                <td className="px-3 py-2">
                  {row.valueType === 'BOOLEAN' ? (
                    <select
                      className="border rounded px-2 py-1"
                      value={String(draftValues[row.key] ?? row.value).toLowerCase()}
                      onChange={(e) => setDraftValues((prev) => ({ ...prev, [row.key]: e.target.value }))}
                    >
                      <option value="true">true</option>
                      <option value="false">false</option>
                    </select>
                  ) : (
                    <input
                      type={row.valueType === 'NUMBER' ? 'number' : 'text'}
                      step={row.valueType === 'NUMBER' ? '1' : undefined}
                      className="border rounded px-2 py-1 w-36"
                      value={draftValues[row.key] ?? row.value}
                      onChange={(e) => setDraftValues((prev) => ({ ...prev, [row.key]: e.target.value }))}
                    />
                  )}
                </td>
                <td className="px-3 py-2">{row.unit || '—'}</td>
                <td className="px-3 py-2">
                  {(row.minValue !== null && row.minValue !== undefined) || (row.maxValue !== null && row.maxValue !== undefined)
                    ? `${row.minValue ?? '-'} .. ${row.maxValue ?? '-'}`
                    : '—'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(row.updatedAt)}</td>
                <td className="px-3 py-2">
                  {row.updatedByUser?.name || row.updatedByUser?.email || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlaySettings;

